// 考勤复核的纯判定逻辑，照搬 be-api ClassAttendancePolicy.reason，只用来给管理员预估结果；最终以后端结算为准。
import type { AttendanceReviewEvidence } from '~/services/attendance-review'

export const TEACHER_SECONDS = 15 * 60
export const STUDENT_SECONDS = 5 * 60
const LESSON_OVERLAP_SECONDS = 15 * 60
const LATE_GRACE_SECONDS = 5 * 60

export type Verdict = 'TAUGHT' | 'TEACHER' | 'STUDENT' | 'BOTH'

/** 后端存的英文缺席原因 → 归类。null 表示正常上课（进入待总结） */
export function verdictOf(reason: string | null): Verdict {
  if (!reason) return 'TAUGHT'
  if (reason.startsWith('Teacher and student')) return 'BOTH'
  if (reason.startsWith('Student')) return 'STUDENT'
  return 'TEACHER'
}

export const verdictLabels: Record<Verdict, string> = {
  TAUGHT: '正常上课（进入待总结）',
  TEACHER: '老师缺席',
  STUDENT: '学生缺席',
  BOTH: '双方缺席',
}
export const verdictColors: Record<Verdict, string> = { TAUGHT: 'green', TEACHER: 'red', STUDENT: 'orange', BOTH: 'red' }

/** 给定时长后的结论；不含「老师开课后才确认」那条改判，那条依赖预约的确认时间 */
export function predictReason(evidence: AttendanceReviewEvidence, teacher: number, student: number, overlap: number | null): Verdict {
  const { started, studentMissed, endedByTeacher, teacherLateSeconds } = evidence
  if (endedByTeacher && teacher < TEACHER_SECONDS && student < STUDENT_SECONDS) return 'TEACHER'
  if (teacher < TEACHER_SECONDS && student < STUDENT_SECONDS) return 'BOTH'
  if (teacher < TEACHER_SECONDS) return 'TEACHER'
  if (student < STUDENT_SECONDS) return 'STUDENT'
  if (!started) return studentMissed ? 'STUDENT' : 'TEACHER'
  if (teacherLateSeconds > LATE_GRACE_SECONDS && (overlap ?? LESSON_OVERLAP_SECONDS) < LESSON_OVERLAP_SECONDS) return 'TEACHER'
  return 'TAUGHT'
}

/** 把无法确认的时间全算作在课时，能得到的最长时长（与后端判定是否需要复核的上界一致） */
export function upperBound(evidence: AttendanceReviewEvidence) {
  const teacher = Math.min(evidence.teacherSeconds + evidence.teacherUnverifiedSeconds, evidence.maxAttendanceSeconds)
  const student = Math.min(evidence.studentSeconds + evidence.studentUnverifiedSeconds, evidence.maxAttendanceSeconds)
  const overlap = Math.min((evidence.overlapSeconds ?? 0) + evidence.teacherUnverifiedSeconds + evidence.studentUnverifiedSeconds,
    evidence.maxOverlapSeconds, teacher, student)
  return { teacher, student, overlap }
}

export function formatDuration(seconds: number | null | undefined): string {
  if (seconds == null) return '—'
  const minutes = Math.floor(seconds / 60)
  const rest = seconds % 60
  return minutes ? `${minutes} 分 ${rest} 秒` : `${rest} 秒`
}
